import { useState, useEffect } from "react";
import { motion } from "framer-motion";

interface ServerErrorPageProps {
  error?: Error | null;
  errorInfo?: any;
  incidentId?: string;
  onReset?: () => void;
}

export function ServerErrorPage({
  error,
  errorInfo,
  incidentId: customIncidentId,
  onReset,
}: ServerErrorPageProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [incidentId] = useState(
    () => customIncidentId || `ERR-${Date.now().toString(36).toUpperCase()}-${Math.floor(1000 + Math.random() * 9000)}`
  );
  const [timestamp] = useState(() => new Date().toISOString());

  useEffect(() => {
    document.title = "500 — Subsystem Error | RADIECE PRIVATE LIMITED";
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute("content", "noindex, nofollow");
    }
  }, []);

  const handleReload = () => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-[80vh] py-16 sm:py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center bg-slate-50 dark:bg-[#070b12] text-slate-900 dark:text-slate-100 transition-colors">
      <div className="max-w-3xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white dark:bg-[#0d1624] border border-slate-200 dark:border-slate-800/80 rounded-3xl p-8 sm:p-12 shadow-xl shadow-slate-900/5 relative overflow-hidden text-center"
        >
          {/* Subtle Ambient Background Glow */}
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rose-500/10 dark:bg-rose-400/10 border border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
            HTTP 500: INTERNAL_SUBSYSTEM_EXCEPTION
          </div>

          {/* Large Hero 500 Code */}
          <h1 className="text-7xl sm:text-8xl lg:text-9xl font-black tracking-tighter text-brand-dark dark:text-white mb-4 select-none">
            <span className="bg-gradient-to-br from-rose-600 via-rose-500 to-amber-500 bg-clip-text text-transparent dark:from-rose-400 dark:via-rose-300 dark:to-amber-300">
              500
            </span>
          </h1>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight mb-4">
            Subsystem Encountered an Unexpected State
          </h2>

          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 font-light max-w-xl mx-auto leading-relaxed mb-6">
            An internal runtime condition occurred while rendering this interface component. Our error dispatch telemetry has cataloged the incident details for technical triage.
          </p>

          {/* Incident Telemetry Card */}
          <div className="inline-flex flex-wrap items-center justify-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-[#080d16] border border-slate-200 dark:border-slate-800 text-xs font-mono text-slate-600 dark:text-slate-400 mb-8">
            <span>
              Incident ID: <strong className="text-slate-800 dark:text-slate-200">{incidentId}</strong>
            </span>
            <span className="text-slate-300 dark:text-slate-700">•</span>
            <span>
              Timestamp: <span className="text-slate-800 dark:text-slate-300">{timestamp}</span>
            </span>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-3.5 mb-8">
            <button
              onClick={handleReload}
              className="btn-shine inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-6 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              ↻ Reload Subsystem
            </button>

            <a
              href="/"
              className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 px-5 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-colors"
            >
              ← Return to Homepage
            </a>

            <a
              href="/security"
              className="inline-flex items-center gap-2 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:text-brand dark:hover:text-white px-5 py-3 rounded-xl font-medium text-xs sm:text-sm transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
            >
              Report to Security Desk →
            </a>
          </div>

          {/* Collapsible Technical Diagnostics */}
          {(error || errorInfo) && (
            <div className="pt-6 border-t border-slate-200/80 dark:border-slate-800/80 text-left">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="inline-flex items-center gap-2 text-xs font-mono font-bold text-slate-500 dark:text-slate-400 hover:text-brand dark:hover:text-primary transition-colors cursor-pointer"
              >
                <span>{showDetails ? "▼ Hide Technical Trace" : "▶ Show Technical Trace"}</span>
              </button>

              {showDetails && (
                <div className="mt-3 p-4 rounded-xl bg-slate-900 text-slate-200 font-mono text-xs overflow-x-auto border border-slate-800 leading-relaxed max-h-60 overflow-y-auto">
                  {error && (
                    <div className="mb-2">
                      <strong className="text-rose-400 font-bold">{error.name}:</strong>{" "}
                      <span className="text-slate-300">{error.message}</span>
                    </div>
                  )}
                  {error?.stack && (
                    <pre className="text-[11px] text-slate-400 whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  )}
                  {errorInfo?.componentStack && (
                    <pre className="mt-2 pt-2 border-t border-slate-800 text-[11px] text-slate-500 whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Corporate Statutory Tagline */}
        <p className="text-center text-xs text-slate-400 dark:text-slate-600 mt-6 font-mono">
          RADIECE PRIVATE LIMITED • CIN: U58200KL2026PTC104862 • SRE Telemetry Gateway
        </p>
      </div>
    </div>
  );
}
